import { forwardRef, memo, useMemo } from 'react';
import { Flex } from '@chakra-ui/react';
import { Tabs, type TabItem } from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import type { InterviewActivityStepProps } from '../InterviewActivity.interface';
import { useCommunicationStep } from './useCommunicationStep';
import {
  CandidateCommunicationPanel,
  InterviewerCommunicationPanel,
  SelfScheduleCommunicationPanel,
} from './panels';

export const InterviewActivityCommunication = memo(
  forwardRef<StepFormApi, InterviewActivityStepProps>(
    ({ payloadRef, prefillVersion = 0 }, ref) => {
      const {
        state,
        dispatch,
        fieldErrors,
        candidateTemplateOptions,
        interviewerTemplateOptions,
        selfScheduleTemplateOptions,
        mergedParticipantOptions,
        panelVariableProps,
      } = useCommunicationStep({ payloadRef, prefillVersion, ref });

      const tabItems: TabItem[] = useMemo(
        () => [
          {
            label: 'Candidate communication',
            value: 'candidate-communication',
            panel: (
              <CandidateCommunicationPanel
                state={state}
                dispatch={dispatch}
                fieldErrors={fieldErrors}
                templateOptions={candidateTemplateOptions}
                fromOptions={mergedParticipantOptions}
                ccBccOptions={mergedParticipantOptions}
                {...panelVariableProps}
              />
            ),
          },
          {
            label: 'Interviewer communication',
            value: 'interviewer-communication',
            panel: (
              <InterviewerCommunicationPanel
                state={state}
                dispatch={dispatch}
                fieldErrors={fieldErrors}
                templateOptions={interviewerTemplateOptions}
                fromOptions={mergedParticipantOptions}
                ccBccOptions={mergedParticipantOptions}
                {...panelVariableProps}
              />
            ),
          },
          {
            label: 'Self-schedule slot selection email',
            value: 'self-schedule-slot-selection-email',
            panel: (
              <SelfScheduleCommunicationPanel
                state={state}
                dispatch={dispatch}
                fieldErrors={fieldErrors}
                templateOptions={selfScheduleTemplateOptions}
                fromOptions={mergedParticipantOptions}
                ccBccOptions={mergedParticipantOptions}
                {...panelVariableProps}
              />
            ),
          },
        ],
        [
          state,
          dispatch,
          fieldErrors,
          candidateTemplateOptions,
          interviewerTemplateOptions,
          selfScheduleTemplateOptions,
          mergedParticipantOptions,
          panelVariableProps,
        ]
      );

      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          minW={0}
          flex="1"
          flexDirection="column"
          overflow="hidden"
        >
          <Tabs.Root
            variant="side"
            items={tabItems}
            value={state.activeSubTab}
            onValueChange={(next) => {
              dispatch({
                type: 'SET_ACTIVE_SUB_TAB',
                payload: next as typeof state.activeSubTab,
              });
            }}
            className="h-full min-h-0 w-full min-w-0 flex-1 grid-rows-[minmax(0,1fr)] gap-3"
            tabListWrapperClassName="self-start"
            panelClassName="min-h-0 h-full max-h-full min-w-0 overflow-y-auto overflow-x-hidden pb-1 pr-4"
          />
        </Flex>
      );
    }
  )
);

InterviewActivityCommunication.displayName = 'InterviewActivityCommunication';
